import { combineReducers } from '@reduxjs/toolkit';
import {
    CommandeApi,
    DossierApi,
    ConfigurationParameters as DossierConfigurationParameters,
    Configuration as DossiersConfiguration,
    FactureVenteApi,
    JournalAnnoncesLegalesApi,
    RubriqueFacturationApi,
    TauxTvaApi,
} from '../../openApi/ApiDossiers';
import { COMMANDES_SLICE_NAME, createCommandeSlice } from './slices/commandeSlice';
import {
    createDossierSlice,
    DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME,
    DOSSIERS_SLICE_NAME,
} from './slices/dossierSlice';
import { createFactureVenteSlice, FACTURE_VENTE_SLICE_NAME } from './slices/factureVenteSlice';
import { createJournalSlice, JOURNAL_SLICE_NAME } from './slices/journalSlice';
import { createRubriqueFacturationSlice, RUBFACT_SLICE_NAME } from './slices/rubriqueFactSlice';
import { createTauxTvaSlice, TXTVA_SLICE_NAME } from './slices/tauxTvaSlice';

export { type DossierConfigurationParameters };

export function createCommandeReducer(configuration: DossierConfigurationParameters) {
    const { commandeSlice, commandeDataSourcesThunks } = createCommandeSlice(configuration);
    return { commandeSlice, commandeDataSourcesThunks };
}

export function createDossierReducer(configuration: DossierConfigurationParameters) {
    const { dossierSlice, dossierDataSourcesThunks } = createDossierSlice(configuration);
    return { dossierSlice, dossierDataSourcesThunks };
}

export function createFactureVenteReducer(configuration: DossierConfigurationParameters) {
    const { factureVenteSlice, factureVenteDataSourcesThunks } =
        createFactureVenteSlice(configuration);
    return { factureVenteSlice, factureVenteDataSourcesThunks };
}

export function createJournalReducer(configuration: DossierConfigurationParameters) {
    const { journalSlice, journalDataSourcesThunks } = createJournalSlice(configuration);
    return { journalSlice, journalDataSourcesThunks };
}

export function createRubriqueFacturationReducer(configuration: DossierConfigurationParameters) {
    const { rubriqueFacturationSlice, rubriqueFacturationDataSourcesThunks } =
        createRubriqueFacturationSlice(configuration);
    return { rubriqueFacturationSlice, rubriqueFacturationDataSourcesThunks };
}

export function createTauxTvaReducer(configuration: DossierConfigurationParameters) {
    const { tauxTvaSlice, tauxTvaDataSourcesThunks } = createTauxTvaSlice(configuration);
    return { tauxTvaSlice, tauxTvaDataSourcesThunks };
}

export const buildDossiersRedux = (configuration: DossierConfigurationParameters) => {
    const dossierConfiguration = new DossiersConfiguration(configuration);

    const dossierApi = new DossierApi(dossierConfiguration);
    const tauxTvaApi = new TauxTvaApi(dossierConfiguration);
    const commandeApi = new CommandeApi(dossierConfiguration);
    const factureVenteApi = new FactureVenteApi(dossierConfiguration);
    const journalAnnoncesLegalesApi = new JournalAnnoncesLegalesApi(dossierConfiguration);
    const rubriqueFacturationApi = new RubriqueFacturationApi(dossierConfiguration);

    const { commandeSlice, commandeDataSourcesThunks } =
        createCommandeReducer(dossierConfiguration);

    const { dossierSlice, dossierDataSourcesThunks } = createDossierReducer(dossierConfiguration);

    const { factureVenteSlice, factureVenteDataSourcesThunks } =
        createFactureVenteReducer(dossierConfiguration);

    const { journalSlice, journalDataSourcesThunks } = createJournalReducer(dossierConfiguration);

    const { rubriqueFacturationSlice, rubriqueFacturationDataSourcesThunks } =
        createRubriqueFacturationReducer(dossierConfiguration);

    const { tauxTvaSlice, tauxTvaDataSourcesThunks } = createTauxTvaReducer(dossierConfiguration);

    const combinedDossiersReducer = combineReducers({
        [COMMANDES_SLICE_NAME]: commandeSlice.reducer,
        [DOSSIERS_SLICE_NAME]: dossierSlice.reducer,
        [FACTURE_VENTE_SLICE_NAME]: factureVenteSlice.reducer,
        [JOURNAL_SLICE_NAME]: journalSlice.reducer,
        [RUBFACT_SLICE_NAME]: rubriqueFacturationSlice.reducer,
        [TXTVA_SLICE_NAME]: tauxTvaSlice.reducer,
    });

    return {
        serviceConfigurationParameters: configuration,
        serviceConfiguration: dossierConfiguration,
        reducer: combinedDossiersReducer,

        apis: {
            dossierApi,
            tauxTvaApi,
            commandeApi,
            factureVenteApi,
            journalAnnoncesLegalesApi,
            rubriqueFacturationApi,
        },

        slices: {
            commandeSlice,
            dossierSlice,
            factureVenteSlice,
            journalSlice,
            rubriqueFacturationSlice,
            tauxTvaSlice,
        },
        thunks: {
            commandeDataSourcesThunks,
            dossierDataSourcesThunks,
            factureVenteDataSourcesThunks,
            journalDataSourcesThunks,
            rubriqueFacturationDataSourcesThunks,
            tauxTvaDataSourcesThunks,
        },
        fetchItems: {
            getCommande: commandeDataSourcesThunks.main.getData,
            getDossier: dossierDataSourcesThunks.main.getData,
            getAutocompleteDossier:
                dossierDataSourcesThunks[DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME].getData,
            getFactureVente: factureVenteDataSourcesThunks.main.getData,
            getJournal: journalDataSourcesThunks.main.getData,
            getRubFac: rubriqueFacturationDataSourcesThunks.main.getData,
            getTauxTva: tauxTvaDataSourcesThunks.main.getData,
        },
        getItems: {
            selectCommande: commandeDataSourcesThunks.main.getSelected,
            selectDossier: dossierDataSourcesThunks.main.getSelected,
            selectAutocompleteDossier:
                dossierDataSourcesThunks[DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME].getSelected,
            selectFactureVente: factureVenteDataSourcesThunks.main.getSelected,
            selectJournal: journalDataSourcesThunks.main.getSelected,
            selectRubFac: rubriqueFacturationDataSourcesThunks.main.getSelected,
            selectTauxTva: tauxTvaDataSourcesThunks.main.getSelected,
        },
        models: {},
    };
};
